import { useState } from 'react'
import {
  Check,
  X,
  Zap,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Shield,
  Globe,
  BarChart3,
  Music,
  MessageSquare,
  FolderKanban,
  RefreshCw,
  Mic2,
  Plane,
  Brain,
  Award,
  Star,
  MapPin,
  Handshake,
  Wifi,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Pitch Deck
   Password-protected, fullscreen slide presentation.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function DeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  /* ── Password Gate ─────────────────────────────────────────────────────── */
  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  /* ── Scrollable Deck ────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      {/* Confidential watermark */}
      <div className="fixed top-6 right-8 z-50">
        <span className="text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>

      {/* All sections rendered sequentially */}
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE DEFINITIONS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

/* ── Slide 1: Title / Introduction ──────────────────────────────────────── */
function SlideTitle() {
  return (
    <div className="flex flex-col justify-center">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-3xl">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.1]">
          The new operating system and source of truth for modern music careers.
        </h1>
      </div>
      <div className="mt-6 max-w-xl">
        <p className="text-sm sm:text-base text-zinc-500 leading-relaxed">
          One platform to manage your entire music career — releases, touring, team, finances, and communication — in real time.
        </p>
      </div>
      <div className="mt-12 flex items-center gap-3">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-1.5 text-[12px] font-medium text-[#FF2300]">
          Pre-Seed
        </span>
        <span className="text-[13px] text-zinc-500">February 2026</span>
      </div>
      <div className="mt-8">
        <p className="text-[12px] text-zinc-600">www.dizee.com</p>
      </div>
    </div>
  )
}

/* ── Slide 2: Macro Trends ──────────────────────────────────────────────── */
function SlideMacroTrends() {
  return (
    <div>
      <SlideTag>Macro Trends</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        The industry has changed.{' '}
        <span className="text-zinc-500">The tools haven&apos;t.</span>
      </h2>

      <div className="mt-12 grid lg:grid-cols-2 gap-12">
        {/* What's Happening Now */}
        <div>
          <h3 className="text-sm font-semibold text-[#FF2300] uppercase tracking-widest mb-6">
            What&apos;s Happening Now
          </h3>
          <ul className="space-y-4">
            <TrendItem>
              <strong className="text-white">Industry Shift</strong> — The music industry is undergoing a seismic technological and cultural shift
            </TrendItem>
            <TrendItem>
              <strong className="text-white">Decentralized Power</strong> — Labels and traditional gatekeepers no longer manage most careers. Power has moved to artists and their teams.
            </TrendItem>
            <TrendItem>
              <strong className="text-white">Independent Era</strong> — Independent artists, managers, and small teams now run the business
            </TrendItem>
            <TrendItem>
              <strong className="text-white">Infrastructure Gap</strong> — Responsibility has shifted, but the operating infrastructure never followed
            </TrendItem>
          </ul>
        </div>

        {/* What's Coming Next */}
        <div>
          <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-widest mb-6">
            What&apos;s Coming Next
          </h3>
          <ul className="space-y-4">
            <TrendItem accent="emerald">
              <strong className="text-white">New AI Tools</strong> — AI and new creation tools are accelerating the number of music makers at an unprecedented rate
            </TrendItem>
            <TrendItem accent="emerald">
              <strong className="text-white">Barrier to Entry</strong> — The barrier to entry for music creators has dramatically decreased, increasing output and complexity
            </TrendItem>
            <TrendItem accent="emerald">
              <strong className="text-white">Rise of the Creators</strong> — The creator economy surpassed $250B in 2024, expanding rapidly into music
            </TrendItem>
            <TrendItem accent="emerald">
              <strong className="text-white">Fragmented Ecosystem</strong> — The gap between how careers operate and the tools supporting them is widening
            </TrendItem>
          </ul>
        </div>
      </div>

      {/* Bottom stat */}
      <div className="mt-12 pt-8 border-t border-white/[0.06]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-12">
          <StatBox value="75M+" label="Music creators active worldwide" />
          <StatBox value="~200M" label="Predicted music creators by 2030" />
          <StatBox value="120K+" label="Tracks uploaded to streaming daily" accent />
          <p className="text-[11px] text-zinc-600 max-w-xs">
            Sources: MIDiA Research 2023, Luminate 2024, NME / Will Page
          </p>
        </div>
      </div>
    </div>
  )
}

/* ── Slide 3: Problem (High Level) ──────────────────────────────────────── */
function SlideProblemHighLevel() {
  return (
    <div>
      <SlideTag>The Problem</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Artists lack clear, real-time visibility{' '}
        <span className="text-red-400">into what&apos;s happening across their career.</span>
      </h2>

      <div className="mt-12 max-w-3xl space-y-8">
        <ProblemStatement icon={<Shield className="w-5 h-5" />}>
          Managers don&apos;t have a centralized system, the time, or reliable access to data needed to make informed decisions. Career-critical information is scattered across dozens of apps and threads.
        </ProblemStatement>

        <ProblemStatement icon={<Zap className="w-5 h-5" />}>
          Today&apos;s tools are slow, fragmented, and static — making it easy to miss moments that matter. By the time data is gathered and organized, the opportunity has passed.
        </ProblemStatement>

        <ProblemStatement icon={<MessageSquare className="w-5 h-5" />}>
          Communication between teams is chaotic — iMessage, email, text threads, and DMs with no single source of truth. Nothing is tracked, searchable, or connected to the work.
        </ProblemStatement>
      </div>

      <div className="mt-12 rounded-xl border border-red-500/20 bg-red-500/[0.04] p-6 max-w-xl">
        <p className="text-lg font-medium text-red-300">
          There is no unified system to manage the modern music business. This leads to missed opportunities and lost revenue.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 4: Problem (Current Tools) ────────────────────────────────────── */
function SlideProblemCurrentTools() {
  return (
    <div>
      <SlideTag>The Problem — Current State</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Today, music businesses still run on:
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <BrokenToolCard icon={<Zap className="w-5 h-5" />} title="Manual Processes">
          Over-complicated workflows, manual data entry, and repetitive tasks that consume hours every week. Nothing is automated or connected.
        </BrokenToolCard>
        <BrokenToolCard icon={<MessageSquare className="w-5 h-5" />} title="Fragmented Communication">
          Spreadsheets, text messages, and email threads. Critical decisions live in iMessage conversations that nobody can search or reference.
        </BrokenToolCard>
        <BrokenToolCard icon={<Shield className="w-5 h-5" />} title="Legacy Tools">
          Tools built for a different era of the music industry. The business moves fast and decisions change constantly — legacy tools can&apos;t keep up.
        </BrokenToolCard>
        <BrokenToolCard icon={<Globe className="w-5 h-5" />} title="Disconnected Systems">
          No single source of truth. Data lives in a dozen different platforms with no way to connect the dots across releases, shows, finances, and team.
        </BrokenToolCard>
      </div>

      <div className="mt-12 max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-white leading-snug">
          Managers don&apos;t have a centralized system, the time, or reliable access to data{' '}
          <span className="text-red-400">to make informed decisions.</span>
        </p>
      </div>
    </div>
  )
}

/* ── Slide 5: Solution ──────────────────────────────────────────────────── */
function SlideSolution() {
  return (
    <div>
      <SlideTag>The Solution</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        DIZEE centralizes the modern music career{' '}
        <span className="text-[#FF2300]">into a single operating system.</span>
      </h2>

      <div className="mt-10 max-w-3xl space-y-6">
        <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
          A single operating system and system of record for the modern music career.
          DIZEE gives artists and managers real-time clarity so they can move faster, make better decisions, and capture revenue as it happens.
        </p>
        <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
          Anyone can now manage a modern music career end-to-end with DIZEE — from catalog and touring to team communication and project management.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-3 gap-6">
        <SolutionCard
          icon={<BarChart3 className="w-5 h-5 text-[#FF2300]" />}
          title="Real-Time Clarity"
          description="A unified view of your entire music career — releases, shows, finances, team — updated in real time, in one place."
        />
        <SolutionCard
          icon={<Brain className="w-5 h-5 text-[#FF2300]" />}
          title="Smarter Decisions"
          description="AI-powered insights and recommendations surface opportunities as they happen so you can act, not react."
        />
        <SolutionCard
          icon={<Target className="w-5 h-5 text-[#FF2300]" />}
          title="End-to-End Management"
          description="From catalog and release management to touring, team communication, and project management — all connected."
        />
      </div>
    </div>
  )
}

/* ── Slide 6: Product — Platform ──────────────────────────────────────────── */
function SlideProductPlatform() {
  return (
    <div>
      <SlideTag>Product — Platform</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        Everything you need to run a modern music career.
      </h2>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-[#FF2300]">
        One platform.
      </h2>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ProductFeature icon={<Brain className="w-4 h-4" />} title="AI Career Assistant" />
        <ProductFeature icon={<BarChart3 className="w-4 h-4" />} title="Dashboard" />
        <ProductFeature icon={<Users className="w-4 h-4" />} title="Teams" />
        <ProductFeature icon={<Music className="w-4 h-4" />} title="Catalog & Releases" />
        <ProductFeature icon={<Mic2 className="w-4 h-4" />} title="Bookings" />
        <ProductFeature icon={<Plane className="w-4 h-4" />} title="Tour Manager" />
        <ProductFeature icon={<FolderKanban className="w-4 h-4" />} title="Project Management" />
        <ProductFeature icon={<RefreshCw className="w-4 h-4" />} title="24/7 Sync" />
      </div>

      <div className="mt-8 border-t border-white/[0.06] pt-8">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-white">
          A unified platform that centralizes your career.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 7: Product — Core Modules ──────────────────────────────────────── */
function SlideProductModules() {
  return (
    <div>
      <SlideTag>Product — Core Modules</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        Built for the full artist career lifecycle.
      </h2>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <ModuleCard
          name="AI Career Assistant"
          description="Answers questions, surfaces insights and recommendations, and automates tasks across your entire career."
          accent="orange"
        />
        <ModuleCard
          name="Dashboard"
          description="A real-time view of all pieces of your music career in one place — streams, revenue, upcoming shows, tasks, and more."
          accent="orange"
        />
        <ModuleCard
          name="Teams"
          description="Connect your team members and associates to streamline communication, assign tasks, and keep everyone aligned."
          accent="orange"
        />
        <ModuleCard
          name="Catalog & Release Management"
          description="Complete discography management — metadata, splits, credits, and release timelines in one system."
          accent="orange"
        />
        <ModuleCard
          name="Bookings"
          description="Tour dates, promoter CRM, revenue insights, calendars, and offer tracking — all connected."
        />
        <ModuleCard
          name="Tour Manager"
          description="Organize and streamline touring operations — travel, itineraries, communication, and logistics."
        />
        <ModuleCard
          name="Project Management"
          description="Tasks, notes, files, and team collaboration for every project — releases, tours, campaigns, and more."
        />
        <ModuleCard
          name="24/7 Sync"
          description="Google Sheets sync, email forwarding, platform integrations, and the DIZEE Email system keep everything connected."
        />
      </div>

      <div className="mt-6 rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.04] p-5">
        <p className="text-[14px] font-medium text-[#FF4500]">
          Anyone can now manage a modern music career end-to-end with DIZEE — every module connects to form a single, intelligent system of record.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 8: Market Size (TAM/SAM/SOM) ──────────────────────────────────── */
function SlideMarketSize() {
  return (
    <div>
      <SlideTag>Market Size</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A massive, underserved market{' '}
        <span className="text-[#FF2300]">growing every year.</span>
      </h2>

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        <MarketCard
          label="TAM"
          sublabel="Total Addressable Market"
          value="$25B+"
          items={[
            'Artist tools, management software, and services — $12B+',
            'Managers, agencies, and labels — $8B+',
            'Touring and promoter infrastructure — $5B+',
          ]}
          color="sky"
        />
        <MarketCard
          label="SAM"
          sublabel="Serviceable Addressable Market"
          value="$12B+"
          items={[
            'Artist management and career tools software',
            'Independent label and manager operations',
            'Touring technology and booking infrastructure',
            'Financial tracking and rights management tools',
          ]}
          color="violet"
        />
        <MarketCard
          label="SOM"
          sublabel="Serviceable Obtainable Market"
          value="$2B+"
          items={[
            'Independent artists and self-managed careers',
            'Artist managers and small management teams',
            'Indie labels seeking unified operations',
            'Booking agents and promoter networks',
          ]}
          color="emerald"
        />
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <p className="text-[13px] text-zinc-400 max-w-lg">
            Independent creators increase this number every year. The adjacent creator economy — now worth over{' '}
            <span className="text-white font-medium">$250B and growing 22%+ annually</span> — represents
            a massive expansion opportunity after music.
          </p>
          <p className="text-[11px] text-zinc-600">
            Sources: MordorIntelligence, Growth Market Reports, MIDiA Research, Future Market Insights
          </p>
        </div>
      </div>
    </div>
  )
}

/* ── Slide 9: Business Model ─────────────────────────────────────────────── */
function SlideBusinessModel() {
  return (
    <div>
      <SlideTag>Business Model</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>

      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        <PricingCard
          tier="Artist"
          price="$45"
          note="/mo"
          audience="Single user — Starter Plan"
          features={[
            'Dashboard',
            'Catalog & Release Management',
            'Career Assistant',
            'Schedule & Calendar',
            'Tasks',
            'DIZEE AI Assistant',
          ]}
        />
        <PricingCard
          tier="Artist Pro"
          price="$75"
          note="/mo"
          audience="Single user — Pro Plan"
          highlighted
          features={[
            'Everything in Artist, plus:',
            'Touring',
            'Teams',
            'Finance tracking',
          ]}
        />
        <PricingCard
          tier="Enterprise"
          price="$125"
          note="/mo"
          audience="2+ users ($15 per additional seat)"
          features={[
            'Everything in Pro, plus:',
            '24/7 Sync',
            'Multi-user access',
            'Priority support',
          ]}
        />
      </div>

      {/* Additional revenue streams */}
      <div className="mt-8">
        <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Additional Revenue Streams</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <RevenueStream>Enterprise plans for agencies and labels</RevenueStream>
          <RevenueStream>AI usage credits</RevenueStream>
          <RevenueStream>Advanced ingestion add-ons</RevenueStream>
          <RevenueStream>Integrations marketplace</RevenueStream>
          <RevenueStream>Financial tools (future)</RevenueStream>
          <RevenueStream>Sponsorship intelligence (future)</RevenueStream>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-white/[0.06]">
        <p className="text-[13px] text-zinc-400">
          <span className="text-white font-medium">High retention</span> due to workflow centralization — once your career data lives in DIZEE, switching costs are high.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 10: Go-To-Market ──────────────────────────────────────────────── */
function SlideGTM() {
  return (
    <div>
      <SlideTag>Go-To-Market</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Industry relationships.{' '}
        <span className="text-[#FF2300]">Strategic distribution.</span>
      </h2>

      <div className="mt-12 grid lg:grid-cols-3 gap-8">
        <GTMChannel
          number="01"
          title="Industry Connections"
          items={[
            'Leverage years of relationships with Grammy-nominated artists',
            'Direct access to platinum-selling acts, agencies, and promoters',
            'Relationships with labels, managers, and industry decision-makers',
            'Music follows trends — early adoption from influential artists accelerates credibility and word-of-mouth growth',
          ]}
        />
        <GTMChannel
          number="02"
          title="Direct Outreach + Partnerships"
          items={[
            'Onboarding artists, managers, and indie labels directly',
            'Collaborations with booking agencies and playlisting groups',
            'Partnerships with marketing companies and service providers',
            'Community building and organic growth via Reddit and social channels',
          ]}
        />
        <GTMChannel
          number="03"
          title="Platform Ecosystem"
          items={[
            'Google Sheets sync fits directly into existing workflows',
            'Email sync and forwarding integrations reduce friction',
            'API partners ensure the product connects to existing tools',
            'DIZEE Email system creates a native communication layer',
          ]}
        />
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <p className="text-[13px] text-zinc-400 max-w-2xl">
          <span className="text-white font-medium">Everyone in music follows the trend.</span> Strong early
          adoption from influential artists sets the trend and drives organic word-of-mouth growth across the industry.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 11: Competition ────────────────────────────────────────────────── */
function SlideCompetition() {
  return (
    <div>
      <SlideTag>Competition</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        From static software{' '}
        <span className="text-[#FF2300]">to an intelligent operating system.</span>
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <div className="rounded-xl border border-red-500/20 bg-red-500/[0.03] p-8">
          <p className="text-[11px] font-semibold text-red-400/60 uppercase tracking-widest mb-4">Today</p>
          <h3 className="text-xl font-semibold text-red-300 mb-4">Static Software</h3>
          <ul className="space-y-3">
            <CompItem negative>Disconnected tools with no unified view of the career</CompItem>
            <CompItem negative>Manual data entry across spreadsheets and apps</CompItem>
            <CompItem negative>Communication scattered across iMessage, email, and DMs</CompItem>
            <CompItem negative>No single source of truth for career data</CompItem>
            <CompItem negative>Built for a different era — can&apos;t keep up with today&apos;s pace</CompItem>
          </ul>
        </div>

        <div className="rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.03] p-8">
          <p className="text-[11px] font-semibold text-[#FF2300]/50 uppercase tracking-widest mb-4">DIZEE</p>
          <h3 className="text-xl font-semibold text-[#FF4500] mb-4">Intelligent Operating System</h3>
          <ul className="space-y-3">
            <CompItem>360° artist career infrastructure in a single platform</CompItem>
            <CompItem>AI-powered automation, insights, and recommendations</CompItem>
            <CompItem>Built-in team communication tied to projects and tasks</CompItem>
            <CompItem>Single source of truth — everything connected in real time</CompItem>
            <CompItem>Purpose-built for the modern independent music career</CompItem>
          </ul>
        </div>
      </div>
    </div>
  )
}

/* ── Slide 12: Competitive Advantages ────────────────────────────────────── */
function SlideAdvantages() {
  return (
    <div>
      <SlideTag>Competitive Advantages</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Why DIZEE wins.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <AdvantageCard icon={<Award className="w-5 h-5 text-[#FF2300]" />}>
          <strong className="text-white">Built by a 2x Grammy-nominated manager</strong> responsible for over 2 billion streams — deep, first-hand understanding of the full artist career lifecycle.
        </AdvantageCard>
        <AdvantageCard icon={<Music className="w-5 h-5 text-emerald-400" />}>
          <strong className="text-white">CEO with proven industry credibility</strong> and an active Billboard-charting record label. Rare combination of operator and domain expertise.
        </AdvantageCard>
        <AdvantageCard icon={<Users className="w-5 h-5 text-violet-400" />}>
          <strong className="text-white">Rare hybrid team</strong> of music insiders and modern product builders — experienced founders with growth-venture operating and capital-raising experience.
        </AdvantageCard>
        <AdvantageCard icon={<Brain className="w-5 h-5 text-amber-400" />}>
          <strong className="text-white">DIZEE AI knowledge base</strong> informed by 15+ years of lived industry experience and real career data — not generic, but music-native intelligence.
        </AdvantageCard>
        <AdvantageCard icon={<Globe className="w-5 h-5 text-[#FF2300]" />}>
          <strong className="text-white">Strong global relationships</strong> across artists, agencies, promoters, and labels — a built-in distribution network from day one.
        </AdvantageCard>
        <AdvantageCard icon={<DollarSign className="w-5 h-5 text-emerald-400" />}>
          <strong className="text-white">Capital-efficient</strong> — agile, lean operating team. Built to move fast, ship product, and scale without excess overhead.
        </AdvantageCard>
        <AdvantageCard icon={<MapPin className="w-5 h-5 text-violet-400" />}>
          <strong className="text-white">Based in Los Angeles</strong> — the center of global music culture, with direct access to the artists, managers, and labels shaping the industry.
        </AdvantageCard>
        <AdvantageCard icon={<TrendingUp className="w-5 h-5 text-amber-400" />}>
          <strong className="text-white">First-mover in a new category.</strong> No one has built a true operating system for the modern music career. DIZEE is defining the space.
        </AdvantageCard>
      </div>
    </div>
  )
}

/* ── Slide 13: Team ──────────────────────────────────────────────────────── */
function SlideTeam() {
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <TeamCard
          name="Ryan Thomas"
          role="CEO"
          points={[
            '15+ years in the dance music industry',
            'Managed Grammy-nominated and platinum-selling artists',
            'Deep global relationships with agencies, promoters, and labels',
          ]}
        />
        <TeamCard
          name="Edward Contreras"
          role="CTO"
          points={[
            'Technologist, product builder, and entrepreneur',
            'Expertise in AI systems, music technology, and multi-product ecosystems',
            "Architect behind DIZEE's platform and integration layer",
          ]}
        />
        <TeamCard
          name="Aaron Wong"
          role="COO"
          points={[
            'Founder of Super73, a globally recognized electric bike company',
            'Experienced in scaling hardware businesses and social presence',
            'Raised and closed multiple rounds of venture funding',
          ]}
        />
        <TeamCard
          name="Owais Ghaus"
          role="CMO"
          points={[
            'Artist, marketer, and technologist',
            'Music executive with deep industry connections',
            'Specializes in content strategy, creator marketing, and artist development',
          ]}
        />
        <TeamCard
          name="Christopher Alba"
          role="CBO"
          points={[
            'Business development and strategic partnerships',
            'Deep network across music and entertainment verticals',
            'Focused on enterprise and label partnerships',
          ]}
        />
      </div>
    </div>
  )
}

/* ── Slide 14: Why Now ──────────────────────────────────────────────────── */
function SlideWhyNow() {
  return (
    <div>
      <SlideTag>Why Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A generational shift.{' '}
        <span className="text-[#FF2300]">The timing is now.</span>
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <WhyNowCard title="A generational industry shift">
          The music industry is undergoing a transformation comparable to analog-to-digital. Independent artists are the
          fastest-growing segment, with non-major labels reaching 29.7% market share in 2024 — their third consecutive year of growth.
        </WhyNowCard>
        <WhyNowCard title="The decline of gatekeepers">
          The rise of DIY creators is reshaping power and workflows. Artists now expect label-grade tools
          without label control. A unified career operating system wasn&apos;t possible before due to fragmentation and low tech adoption.
        </WhyNowCard>
        <WhyNowCard title="Complexity is increasing">
          Touring, marketing, and release cycles are more complex and demanding than ever. Managers must
          scale operations efficiently to stay profitable. More music is released in a single day than in the entire year of 1989.
        </WhyNowCard>
        <WhyNowCard title="Technology readiness has converged">
          AI, cloud infrastructure, and API ecosystems have matured to the point where building a true career operating system
          is finally possible. Technology, culture, and industry readiness have converged.
        </WhyNowCard>
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <p className="text-lg font-medium text-white max-w-2xl">
          The industry is ready for a single, modern career operating system.
        </p>
        <p className="text-[11px] text-zinc-600 mt-2">
          Sources: MIDiA Research 2024, NME / Will Page Study, MordorIntelligence Independent Artists Report
        </p>
      </div>
    </div>
  )
}

/* ── Slide 15: The Ask ──────────────────────────────────────────────────── */
function SlideAsk() {
  return (
    <div>
      <SlideTag>The Ask</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Raising <span className="text-[#FF2300]">$2.5M</span> to accelerate.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <UseOfFundsCard
          icon={<Target className="w-5 h-5 text-[#FF2300]" />}
          title="Product–Market Fit"
          description="Validate with real artists and managers — iterating on the product with active users in the music industry"
        />
        <UseOfFundsCard
          icon={<Shield className="w-5 h-5 text-[#FF2300]" />}
          title="Core Platform Development"
          description="System hardening, performance, and reliability — building a platform that scales with the industry"
        />
        <UseOfFundsCard
          icon={<Wifi className="w-5 h-5 text-[#FF2300]" />}
          title="Deep Integrations"
          description="Connecting across the music ecosystem — streaming platforms, booking systems, financial tools"
        />
        <UseOfFundsCard
          icon={<Star className="w-5 h-5 text-[#FF2300]" />}
          title="Marquee Artist Onboarding"
          description="Onboarding high-profile artists and management partners to drive credibility and adoption"
        />
        <UseOfFundsCard
          icon={<Users className="w-5 h-5 text-[#FF2300]" />}
          title="Founding Team & Execution"
          description="Key hires and founding team focus — ensuring the team can execute at the pace the market demands"
        />
        <UseOfFundsCard
          icon={<Handshake className="w-5 h-5 text-[#FF2300]" />}
          title="Infrastructure & Compliance"
          description="Secure, scalable infrastructure — data protection, compliance, and beta expansion"
        />
      </div>

      <div className="mt-12 rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.04] p-6 max-w-2xl">
        <p className="text-lg font-medium text-white">
          DIZEE is building the modern operating system the music industry has been missing.
        </p>
        <p className="mt-2 text-[13px] text-zinc-400">
          The team is built. The product is in motion. Now it&apos;s about capturing the market.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 16: Closing ──────────────────────────────────────────────────── */
function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The new operating system and source of truth for modern music careers.
        </p>
      </div>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">partners@dizee.com</p>
        <p className="text-[13px] text-zinc-500">www.dizee.com</p>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE REGISTRY
   ═══════════════════════════════════════════════════════════════════════════ */

const SLIDES: Slide[] = [
  { title: 'Introduction', component: SlideTitle },
  { title: 'Macro Trends', component: SlideMacroTrends },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Problem — Current State', component: SlideProblemCurrentTools },
  { title: 'Solution', component: SlideSolution },
  { title: 'Product — Platform', component: SlideProductPlatform },
  { title: 'Product — Modules', component: SlideProductModules },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Competitive Advantages', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'Why Now', component: SlideWhyNow },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing', component: SlideClosing },
]

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

function StatBox({
  value,
  label,
  accent = false,
}: {
  value: string
  label: string
  accent?: boolean
}) {
  return (
    <div>
      <p className={`text-3xl sm:text-4xl font-bold tracking-tight ${accent ? 'text-[#FF2300]' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-[13px] text-zinc-500 mt-1">{label}</p>
    </div>
  )
}

function TrendItem({
  children,
  accent = 'orange',
}: {
  children: React.ReactNode
  accent?: 'orange' | 'emerald'
}) {
  return (
    <li className="flex items-start gap-3">
      <div className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${accent === 'emerald' ? 'bg-emerald-400' : 'bg-[#FF2300]'}`} />
      <p className="text-[14px] text-zinc-300 leading-relaxed">{children}</p>
    </li>
  )
}

function ProblemStatement({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-1 shrink-0 text-red-400/60">{icon}</div>
      <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">{children}</p>
    </div>
  )
}

function BrokenToolCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-red-500/10 bg-red-500/[0.03] p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="text-red-400/60">{icon}</div>
        <h3 className="text-[15px] font-semibold text-red-300">{title}</h3>
      </div>
      <p className="text-[13px] text-red-300/50 leading-relaxed">{children}</p>
    </div>
  )
}

function SolutionCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <div className="mb-3">{icon}</div>
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[13px] text-zinc-400 leading-relaxed">{description}</p>
    </div>
  )
}

function ProductFeature({
  icon,
  title,
}: {
  icon: React.ReactNode
  title: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <div className="text-[#FF2300] shrink-0">{icon}</div>
      <p className="text-[13px] font-medium text-white">{title}</p>
    </div>
  )
}

function ModuleCard({
  name,
  description,
  accent = 'white',
}: {
  name: string
  description: string
  accent?: 'orange' | 'white'
}) {
  return (
    <div className={`rounded-lg border p-4 ${
      accent === 'orange'
        ? 'border-[#FF2300]/20 bg-[#FF2300]/[0.04]'
        : 'border-white/[0.06] bg-white/[0.02]'
    }`}>
      <h3 className={`text-[13px] font-semibold mb-1 ${accent === 'orange' ? 'text-[#FF4500]' : 'text-white'}`}>
        {name}
      </h3>
      <p className="text-[11px] text-zinc-500 leading-relaxed">{description}</p>
    </div>
  )
}

function MarketCard({
  label,
  sublabel,
  value,
  items,
  color,
}: {
  label: string
  sublabel: string
  value: string
  items: string[]
  color: 'sky' | 'violet' | 'emerald'
}) {
  const colors = {
    sky: { border: 'border-[#FF2300]/20', bg: 'bg-[#FF2300]/[0.04]', text: 'text-[#FF2300]', dot: 'bg-[#FF2300]' },
    violet: { border: 'border-violet-500/20', bg: 'bg-violet-500/[0.04]', text: 'text-violet-400', dot: 'bg-violet-400' },
    emerald: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/[0.04]', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  }
  const c = colors[color]

  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-6`}>
      <p className={`text-[11px] font-semibold uppercase tracking-widest ${c.text} mb-1`}>{label}</p>
      <p className="text-[11px] text-zinc-600 mb-4">{sublabel}</p>
      <p className={`text-4xl font-bold tracking-tight ${c.text} mb-6`}>{value}</p>
      <ul className="space-y-2.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${c.dot}`} />
            <p className="text-[12px] text-zinc-400 leading-relaxed">{item}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PricingCard({
  tier,
  price,
  audience,
  highlighted = false,
  note,
  features,
}: {
  tier: string
  price: string
  audience: string
  highlighted?: boolean
  note: string
  features: string[]
}) {
  return (
    <div className={`rounded-xl border p-5 ${
      highlighted
        ? 'border-[#FF2300]/30 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20'
        : 'border-white/[0.06] bg-white/[0.02]'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-white">{tier}</h3>
        {highlighted && (
          <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">
            Popular
          </span>
        )}
      </div>
      <p className="text-2xl font-bold tracking-tight text-white">
        {price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{note}</span>
      </p>
      <p className="text-[11px] text-[#FF2300] mt-1 font-medium">{audience}</p>
      <ul className="mt-4 space-y-2">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 text-zinc-600 shrink-0" />
            <p className="text-[12px] text-zinc-400 leading-relaxed">{feature}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function RevenueStream({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <DollarSign className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      <p className="text-[12px] text-zinc-400">{children}</p>
    </div>
  )
}

function GTMChannel({
  number,
  title,
  items,
}: {
  number: string
  title: string
  items: string[]
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <span className="text-[11px] font-mono text-[#FF2300]/50 mb-3 block">{number}</span>
      <h3 className="text-[15px] font-semibold text-white mb-4">{title}</h3>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 bg-[#FF2300]" />
            <p className="text-[13px] text-zinc-400 leading-relaxed">{item}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function CompItem({
  children,
  negative = false,
}: {
  children: React.ReactNode
  negative?: boolean
}) {
  return (
    <li className="flex items-start gap-2.5">
      {negative ? (
        <X className="w-4 h-4 mt-0.5 text-red-400/50 shrink-0" />
      ) : (
        <Check className="w-4 h-4 mt-0.5 text-[#FF2300] shrink-0" />
      )}
      <p className={`text-[13px] leading-relaxed ${negative ? 'text-red-300/60' : 'text-[#FF4500]/80'}`}>
        {children}
      </p>
    </li>
  )
}

function AdvantageCard({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="shrink-0 mt-0.5">{icon}</div>
      <p className="text-[13px] text-zinc-300 leading-relaxed">{children}</p>
    </div>
  )
}

function TeamCard({
  name,
  role,
  points,
}: {
  name: string
  role: string
  points: string[]
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2300]/30 to-[#FF4500]/20 border border-white/[0.08] flex items-center justify-center">
          <span className="text-[13px] font-semibold text-white">
            {name.split(' ').map((n) => n[0]).join('')}
          </span>
        </div>
        <div>
          <h3 className="text-[14px] font-semibold text-white">{name}</h3>
          <p className="text-[11px] text-[#FF2300] font-medium">{role}</p>
        </div>
      </div>
      <ul className="space-y-2">
        {points.map((point, i) => (
          <li key={i} className="flex items-start gap-2">
            <div className="mt-1.5 h-1 w-1 rounded-full shrink-0 bg-zinc-600" />
            <p className="text-[12px] text-zinc-400 leading-relaxed">{point}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WhyNowCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[13px] text-zinc-400 leading-relaxed">{children}</p>
    </div>
  )
}

function UseOfFundsCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="mb-2">{icon}</div>
      <h3 className="text-[14px] font-semibold text-white mb-1">{title}</h3>
      <p className="text-[12px] text-zinc-400 leading-relaxed">{description}</p>
    </div>
  )
}
